<template>
  <div class="organisation-settings">
    <HeaderBar :modifiers="['transparent', 'form']">
      <div class="header-bar__title typo-title">
        {{ organisationId ? $t('nav.edit_organisation') :$t('nav.new_organisation') }}
      </div>

      <template #right>
        <BackLink :to="backRoute" class="button">
          {{ $t('general.cancel') }}
        </BackLink>

        <FormSubmitButton @click="save" :isSaving="saving" />
      </template>
    </HeaderBar>

    <div class="form-layout" v-if="!loading">
      <div class="form-layout__heading">
        <h2 class="form-layout__title typo-small-title">
          {{ $t('organisations.details_title') }}
        </h2>
        <p class="form-layout__description typo-small-title">
          {{ $t('organisations.details_description') }}
        </p>
      </div>

      <div class="form-layout__content">
        <FormRow
          :modifiers="['no-padding']"
          :label="$t('organisations.organisation_name_label')"
          labelFor="organisation_name"
          :errors="errors && errors.organisation.name"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ 'input': true, 'input--error': errors && errors.organisation.name }">
              <input type="text" id="organisation_name" v-model="organisation.name" />
            </div>
          </div>
        </FormRow>
      </div>
    </div>

    <div class="form-layout" v-if="!loading">
      <div class="form-layout__heading">
        <h2 class="form-layout__title typo-small-title">
          {{ $t('organisations.logo_title') }}
        </h2>
        <p class="form-layout__description typo-small-title">
          {{ $t('organisations.logo_description') }}
        </p>
      </div>

      <div class="form-layout__content organisation-settings__logo-section">
        <div class="organisation-settings__logo-wrapper organisation-logo" :data-content="organisation.name ? organisation.name[0].toUpperCase() : ''">
          <img v-if="organisation.logo" class="organisation-settings__logo" :src="organisation.logo[100]" />
        </div>
        <div class="organisation-settings__logo-actions">
          <div class="organisation-settings__logo-input-wrapper" v-if="!organisation.logo">
            <label for="organisation_logo" class="organisation-settings__logo-input-button button">
              {{ $t('organisations.upload_image') }}
            </label>
            <input type="file" id="organisation_logo" class="organisation-settings__logo-input" @change="selectLogo" />
          </div>
          <button v-if="organisation.logo" class="button" type="button" @click="removeLogo">
            {{ $t('organisations.remove_image') }}
          </button>
        </div>
      </div>
    </div>
    <Loader v-if="loading" class="organisation-settings__loader" />
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import useUploader from '@/vendor/use_uploader';

import useAjax from '@/functions/use_ajax';
import readFile from '@/functions/read_file';
import { capitalize } from '@/functions/utils';

import BackLink from '@/components/generic/back_link.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import FormRow from '@/components/form/row.vue';

import FormSubmitButton from '@/components/form/form_submit_button.vue';

const props = defineProps({
  organisationId: { type: String, default: null },
});

const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();

const loading = ref(true);
const organisation = reactive(createNewOrganisation());
const errors = ref(null);
const saving = ref(false);

let newLogoUpload = null;

const backRoute = computed(() => {
  if (props.organisationId) {
    return { name: 'operations' };
  } else {
    return { name: 'root' };
  }
});

onMounted(async () => {
  loading.value = true;
  if (props.organisationId) {
    let { ok, data } = await ajax(`/organisations/${props.organisationId}`);

    if (ok) {
      Object.assign(organisation, data);
    }
  }
  loading.value = false;
});

function createNewOrganisation() {
  return {
    name: '',
    logo: null,
    newLogo: null,
  };
}

/**
 * @param {{ target: { files: File[] }}} param
 */
async function selectLogo({ target: { files: [file] } }) {
  if (!file) return;

  let url = await readFile(file);

  organisation.logo = { 32: url, 72: url, 100: url };
  newLogoUpload = uploader.create(file);
}

function removeLogo() {
  newLogoUpload = null;
  organisation.logo = null;
}

async function formatOrganisationForPost() {
  let formattedOrganisation = {
    name: organisation.name,
  };

  if (newLogoUpload) {
    let blob = await uploader.upload(newLogoUpload);
    formattedOrganisation.logo = blob.signed_id;
  } else if (!organisation.logo && props.organisationId) {
    formattedOrganisation.remove_logo = true;
  }

  return { organisation: formattedOrganisation };
}

async function save() {
  saving.value = true;
  errors.value = null;

  let url = props.organisationId ? `/organisations/${props.organisationId}` : '/organisations';
  let method = props.organisationId ? 'PATCH' : 'POST';
  let body = await formatOrganisationForPost();

  let { ok, data } = await ajax(url, { method, body });

  if (ok) {
    router.replace({ name: 'operations', params: { organisationId: data.id } });
  } else {
    errors.value = data;
  }

  saving.value = false;
}
</script>
